import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";
import { Badge } from "dash-ui";
import { BrandingTypeEnum } from "gql/generated.graphql";
import { SystemMediaFoldersTypesEnum } from "dash-common";

interface BrandingTypeBadgeProps {
  brandingType?: BrandingTypeEnum | null;
  systemFolderType?: SystemMediaFoldersTypesEnum | null;
  isFolder?: boolean;
}

const BrandingTypeBadge = ({
  brandingType,
  systemFolderType,
  isFolder,
}: BrandingTypeBadgeProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  // Custom color schemes using theme colors
  const colorSchemes = {
    branded: {
      bgColor: "#FFF3CD", // Light yellow (better contrast)
      titleColor: "#856404", // Dark yellow/brown (better contrast)
    },
    unbranded: {
      bgColor: theme.colors.b20, // Light blue (same as Draft status)
      titleColor: theme.colors.b50, // Medium blue (same as Draft status)
    },
    shared: {
      bgColor: theme.colors.g10, // Light green (same as Public status)
      titleColor: theme.colors.g20, // Dark green (same as Public status)
    },
  };

  // For folders in Image/Slideshow system folders
  if (
    isFolder &&
    (systemFolderType === SystemMediaFoldersTypesEnum.Images ||
      systemFolderType === SystemMediaFoldersTypesEnum.Slideshow)
  ) {
    return (
      <Badge 
        title={t("common:labels.sharedFolder")} 
        bgColor={colorSchemes.shared.bgColor}
        titleColor={colorSchemes.shared.titleColor}
      />
    );
  }

  // For files
  if (!isFolder && brandingType) {
    const colorMap = {
      [BrandingTypeEnum.Branded]: colorSchemes.branded,
      [BrandingTypeEnum.Unbranded]: colorSchemes.unbranded,
      [BrandingTypeEnum.Shared]: colorSchemes.shared,
    };

    const labels: Record<string, string> = {
      [BrandingTypeEnum.Branded]: t("common:labels.branded"),
      [BrandingTypeEnum.Unbranded]: t("common:labels.unbranded"),
      [BrandingTypeEnum.Shared]: t("common:labels.shared"),
    };

    const colors = colorMap[brandingType];

    return (
      <Badge 
        title={labels[brandingType]} 
        bgColor={colors.bgColor}
        titleColor={colors.titleColor}
      />
    );
  }

  // Legacy files without brandingType are considered unbranded
  if (!isFolder && !brandingType) {
    return (
      <Badge 
        title={t("common:labels.unbranded")} 
        bgColor={colorSchemes.unbranded.bgColor}
        titleColor={colorSchemes.unbranded.titleColor}
      />
    );
  }

  return null;
};

export default BrandingTypeBadge;